import React, { useEffect } from "react";
import { useWatch } from "react-hook-form";
import { drugUseRows } from "../config/drugUseConfig";
import { todayISO } from "../config/dateUtils";

const LEVEL_OPTIONS = [
  "Abstinence",
  "Single use",
  "Once or twice a month",
  "A few times a month",
  "Once or twice a week",
  "A few times a week",
  "Most days of the week",
  "Daily",
  "Unsure",
];

function isoMonthsAgo(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

function blankPeriod() {
  return {
    start_date_of_use: "",
    date_of_last_use: "",
    unsure_date: false,
    level_of_use: "",
    prescribed: false,
    has_change: false,
  };
}

function blankDrugUseRow(drug, status = "Not Used") {
  return {
    drug_name: drug.name,
    status,
    start_date_of_use: "",
    date_of_last_use: "",
    unsure_date: false,
    level_of_use: "",
    prescribed: false,
    has_change: false,
    periods: [],
  };
}

function isEmptyPeriod(p) {
  if (!p) return true;
  return (
    !p.has_change &&
    !p.start_date_of_use &&
    !p.date_of_last_use &&
    !p.level_of_use &&
    !p.unsure_date &&
    !p.prescribed
  );
}

function isPeriodActive(p) {
  if (!p) return false;
  return (
    !!p.has_change ||
    !!p.start_date_of_use ||
    !!p.date_of_last_use ||
    !!p.level_of_use ||
    !!p.unsure_date ||
    !!p.prescribed
  );
}

function DateShortcuts({ onToday, onMinus3, onMinus6, onMinus12 }) {
  return (
    <div style={styles.dateShortcuts}>
      <button type="button" style={styles.shortcutBtn} onClick={onToday}>
        Today
      </button>
      <button type="button" style={styles.shortcutBtn} onClick={onMinus3}>
        −3 months
      </button>
      <button type="button" style={styles.shortcutBtn} onClick={onMinus6}>
        −6 months
      </button>
      <button type="button" style={styles.shortcutBtn} onClick={onMinus12}>
        −12 months
      </button>
    </div>
  );
}

function DrugUseRow({ drug, index, register, errors, showErrors, control, setValue }) {
  const baseHasChange =
    useWatch({ control, name: `drug_use.${index}.has_change` }) || false;

  const periods =
    useWatch({ control, name: `drug_use.${index}.periods` }) || [];

  const parentStart =
    useWatch({ control, name: `drug_use.${index}.start_date_of_use` }) || "";

  const parentEnd =
    useWatch({ control, name: `drug_use.${index}.date_of_last_use` }) || "";

  const parentUnsure =
    useWatch({ control, name: `drug_use.${index}.unsure_date` }) || false;

  const parentErr = errors?.drug_use?.[index] || {};


  useEffect(() => {
    if (baseHasChange) {
      if (!Array.isArray(periods) || periods.length === 0) {
        setValue(`drug_use.${index}.periods`, [blankPeriod()], {
          shouldDirty: false,
        });
      }
    } else {
      if (Array.isArray(periods) && periods.length > 0) {
        setValue(`drug_use.${index}.periods`, [], { shouldDirty: false });
      }
    }
  }, [baseHasChange, periods, index, setValue]);

  useEffect(() => {
    if (!Array.isArray(periods) || periods.length === 0) return;

    let lastTicked = -1;

    for (let i = 0; i < periods.length; i++) {
      if (periods[i]?.has_change) lastTicked = i;
    }

    if (lastTicked === -1) {
      if (periods.length !== 1) {
        setValue(`drug_use.${index}.periods`, [periods[0] || blankPeriod()], {
          shouldDirty: false,
        });
      }
      return;
    }

    const desiredLen = lastTicked + 2;

    if (periods.length < desiredLen) {
      const next = [...periods];
      while (next.length < desiredLen) next.push(blankPeriod());
      setValue(`drug_use.${index}.periods`, next, { shouldDirty: false });
      return;
    }

    if (periods.length > desiredLen) {
      const tail = periods.slice(desiredLen);
      const allTailEmpty = tail.every(isEmptyPeriod);

      if (allTailEmpty) {
        setValue(`drug_use.${index}.periods`, periods.slice(0, desiredLen), {
          shouldDirty: false,
        });
      }
    }
  }, [periods, index, setValue]);

  return (
    <>
      <tr>
        <td style={styles.td}>{drug.name}</td>

        {/* Parent start date */}
        <td style={styles.td}>
          <input
            style={styles.input}
            type="date"
            max={todayISO}
            {...register(`drug_use.${index}.start_date_of_use`, {
              validate: (v) => {
                if (parentUnsure) return true;
                return (v || "").trim()
                  ? true
                  : "Start date is required, or tick Unsure";
              },
            })}
          />

          <DateShortcuts
            onToday={() =>
              setValue(`drug_use.${index}.start_date_of_use`, todayISO, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus3={() =>
              setValue(`drug_use.${index}.start_date_of_use`, isoMonthsAgo(3), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus6={() =>
              setValue(`drug_use.${index}.start_date_of_use`, isoMonthsAgo(6), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus12={() =>
              setValue(`drug_use.${index}.start_date_of_use`, isoMonthsAgo(12), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        </td>

        {/* Parent end date */}
        <td style={styles.td}>
          <input
            style={styles.input}
            type="date"
            max={todayISO}
            min={parentStart || undefined}
            {...register(`drug_use.${index}.date_of_last_use`, {
              validate: (end) => {
                if (parentUnsure) return true;

                if (!(end || "").trim()) {
                  return "End date is required, or tick Unsure";
                }

                if (parentStart && end < parentStart) {
                  return "End date cannot be before start date";
                }

                return true;
              },
            })}
          />

          <DateShortcuts
            onToday={() =>
              setValue(`drug_use.${index}.date_of_last_use`, todayISO, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus3={() =>
              setValue(`drug_use.${index}.date_of_last_use`, isoMonthsAgo(3), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus6={() =>
              setValue(`drug_use.${index}.date_of_last_use`, isoMonthsAgo(6), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onMinus12={() =>
              setValue(`drug_use.${index}.date_of_last_use`, isoMonthsAgo(12), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        </td>

        {/* Parent unsure */}
        <td style={styles.tdCenter}>
          <input type="checkbox" {...register(`drug_use.${index}.unsure_date`)} />
        </td>

        {/* Parent level of use */}
        <td style={styles.td}>
          <select
            style={styles.select}
            {...register(`drug_use.${index}.level_of_use`, {
              validate: (v) =>
                (v || "").trim() ? true : "Level of use is required",
            })}
          >
            <option value="">Choose</option>
            {LEVEL_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </td>

        {/* Parent prescribed */}
        <td style={styles.tdCenter}>
          {drug.prescribed ? (
            <input type="checkbox" {...register(`drug_use.${index}.prescribed`)} />
          ) : (
            <span style={styles.muted}>—</span>
          )}
        </td>

        {/* Parent changes/fluctuations */}
        <td style={styles.tdCenter}>
          <input type="checkbox" {...register(`drug_use.${index}.has_change`)} />
        </td>

        <input
          type="hidden"
          value={drug.name}
          {...register(`drug_use.${index}.drug_name`)}
        />

        <input
          type="hidden"
          value="Used"
          {...register(`drug_use.${index}.status`)}
        />
      </tr>

      {showErrors &&
        (parentErr?.start_date_of_use ||
          parentErr?.date_of_last_use ||
          parentErr?.level_of_use) && (
          <tr>
            <td style={styles.errorRow} colSpan={7}>
              <span style={styles.errorText}>
                {drug.name}:{" "}
                {parentErr.start_date_of_use?.message ||
                  parentErr.date_of_last_use?.message ||
                  parentErr.level_of_use?.message}
              </span>
            </td>
          </tr>
        )}

      {/* Period rows */}
      {Array.isArray(periods) &&
        periods.length > 0 &&
        periods.map((p, pIndex) => {
          const pErr = errors?.drug_use?.[index]?.periods?.[pIndex] || {};
          const periodActive = isPeriodActive(p);
          const periodStart = (p?.start_date_of_use || "").trim();

          return (
            <React.Fragment key={`${drug.name}-period-${pIndex}`}>
              <tr>
                <td style={{ ...styles.td, ...styles.subRowDrug }}>
                  <span style={styles.subRowArrow}>↳</span> Period {pIndex + 2}
                </td>

                {/* Period start date */}
                <td style={styles.td}>
                  <input
                    style={styles.input}
                    type="date"
                    max={todayISO}
                    {...register(`drug_use.${index}.periods.${pIndex}.start_date_of_use`, {
                      validate: (v) => {
                        if (!baseHasChange) return true;
                        if (p?.unsure_date) return true;

                        return (v || "").trim()
                          ? true
                          : "Start date is required, or tick Unsure";
                      },
                    })}
                  />

                  <DateShortcuts
                    onToday={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.start_date_of_use`,
                        todayISO,
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus3={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.start_date_of_use`,
                        isoMonthsAgo(3),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus6={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.start_date_of_use`,
                        isoMonthsAgo(6),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus12={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.start_date_of_use`,
                        isoMonthsAgo(12),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                  />
                </td>

                {/* Period end date */}
                <td style={styles.td}>
                  <input
                    style={styles.input}
                    type="date"
                    max={todayISO}
                    min={periodStart || undefined}
                    {...register(`drug_use.${index}.periods.${pIndex}.date_of_last_use`, {
                      validate: (end) => {
                        if (!baseHasChange) return true;
                        if (p?.unsure_date) return true;

                        if (!(end || "").trim()) {
                          return "End date is required, or tick Unsure";
                        }

                        if (periodStart && end < periodStart) {
                          return "End date cannot be before start date";
                        }

                        return true;
                      },
                    })}
                  />

                  <DateShortcuts
                    onToday={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.date_of_last_use`,
                        todayISO,
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus3={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.date_of_last_use`,
                        isoMonthsAgo(3),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus6={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.date_of_last_use`,
                        isoMonthsAgo(6),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    onMinus12={() =>
                      setValue(
                        `drug_use.${index}.periods.${pIndex}.date_of_last_use`,
                        isoMonthsAgo(12),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                  />
                </td>

                {/* Period unsure */}
                <td style={styles.tdCenter}>
                  <input
                    type="checkbox"
                    {...register(`drug_use.${index}.periods.${pIndex}.unsure_date`)}
                  />
                </td>

                {/* Period level of use */}
                <td style={styles.td}>
                  <select
                    style={styles.select}
                    {...register(`drug_use.${index}.periods.${pIndex}.level_of_use`, {
                      validate: (v) => {
                        if (!baseHasChange) return true;

                        return (v || "").trim()
                          ? true
                          : "Level of use is required";
                      },
                    })}
                  >
                    <option value="">Choose</option>
                    {LEVEL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Period prescribed */}
                <td style={styles.tdCenter}>
                  {drug.prescribed ? (
                    <input
                      type="checkbox"
                      {...register(`drug_use.${index}.periods.${pIndex}.prescribed`)}
                    />
                  ) : (
                    <span style={styles.muted}>—</span>
                  )}
                </td>

                {/* Period changes/fluctuations */}
                <td style={styles.tdCenter}>
                  <input
                    type="checkbox"
                    {...register(`drug_use.${index}.periods.${pIndex}.has_change`)}
                  />
                </td>
              </tr>

              {showErrors &&
                (pErr?.start_date_of_use ||
                  pErr?.date_of_last_use ||
                  pErr?.level_of_use) && (
                  <tr>
                    <td style={styles.errorRow} colSpan={7}>
                      <span style={styles.errorText}>
                        {drug.name} period {pIndex + 2}:{" "}
                        {pErr.start_date_of_use?.message ||
                          pErr.date_of_last_use?.message ||
                          pErr.level_of_use?.message}
                      </span>
                    </td>
                  </tr>
                )}
            </React.Fragment>
          );
        })}
    </>
  );
}

export default function DrugUseTable({ register, control, setValue, errors, showErrors }) {
  const selectedUseDrugs =
    useWatch({ control, name: "drug_use_selected" }) || [];

  const selectedSet = new Set(selectedUseDrugs);

  useEffect(() => {
    drugUseRows.forEach((drug, index) => {
      const selected = selectedSet.has(drug.name);

      if (selected) {
        setValue(`drug_use.${index}.drug_name`, drug.name, {
          shouldDirty: false,
        });
        setValue(`drug_use.${index}.status`, "Used", {
          shouldDirty: false,
        });
      } else {
        setValue(`drug_use.${index}`, blankDrugUseRow(drug, "Not Used"), {
          shouldDirty: false,
        });
      }
    });
  }, [selectedUseDrugs.join("|"), setValue]);

  return (
    <section style={styles.section}>
      <h2 style={styles.h2}>Drug use</h2>

      <p style={styles.infoText}>
        <strong>Drug Use:</strong> Please tick the drugs you have used and complete the table below. The table below will only
        show those selected drugs. Any drugs not selected will be treated as not used.
      </p>

      <div style={styles.drugPickerBox}>
        <label style={styles.otherInfoLabel}>
          Which drugs have you used? Tick all that apply.
        </label>

        <div style={styles.drugPickerGrid}>
          {drugUseRows.map((drug) => (
            <label key={drug.name} style={styles.checkboxLabel}>
              <input
                type="checkbox"
                value={drug.name}
                {...register("drug_use_selected")}
              />
              <span>{drug.name}</span>
            </label>
          ))}
        </div>

        {selectedUseDrugs.length === 0 && (
          <div style={styles.helperText}>
            Select one or more drugs to show the use table below.
          </div>
        )}
      </div>

      {selectedUseDrugs.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Drug</th>
                <th style={styles.th}>Start date</th>
                <th style={styles.th}>End date</th>
                <th style={styles.thCenter}>Unsure</th>
                <th style={styles.th}>Level of use</th>
                <th style={styles.thCenter}>Prescribed to you</th>
                <th style={styles.thCenter}>Any changes/fluctuations in use</th>
              </tr>
            </thead>

            <tbody>
              {drugUseRows.map((drug, index) => {
                if (!selectedSet.has(drug.name)) return null;

                return (
                  <DrugUseRow
                    key={drug.name}
                    drug={drug}
                    index={index}
                    register={register}
                    errors={errors}
                    showErrors={showErrors}
                    control={control}
                    setValue={setValue}
                  />
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td style={styles.td} colSpan={7}>
                  <label style={styles.otherInfoLabel}>Other information, including details of any other illicit substances not listed</label>
                  <textarea
                    style={styles.textarea}
                    rows={4}
                    {...register("drug_use_other_info")}
                    placeholder="Enter any additional information here..."
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

const styles = {
  section: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  h2: { marginBottom: 8, color: "#904369" },
  infoText: { marginTop: 0, marginBottom: 12, lineHeight: 1.35 },

  drugPickerBox: {
    border: "1px solid #eee",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    background: "#fafafa",
  },
  drugPickerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
    marginTop: 8,
  },
  helperText: {
    marginTop: 8,
    color: "#666",
    fontSize: 12,
  },

  tableWrapper: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    minWidth: 0,
    tableLayout: "fixed",
  },

  th: {
    textAlign: "left",
    padding: 10,
    borderBottom: "1px solid #ddd",
    background: "#fafafa",
    whiteSpace: "nowrap",
  },
  thCenter: {
    textAlign: "center",
    padding: 6,
    borderBottom: "1px solid #ddd",
    background: "#fafafa",
    whiteSpace: "normal",
  },

  td: {
    padding: 10,
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
  },
  tdCenter: {
    padding: 6,
    borderBottom: "1px solid #eee",
    textAlign: "center",
    verticalAlign: "middle",
  },

  input: {
    padding: 6,
    borderRadius: 6,
    border: "1px solid #ccc",
    width: "100%",
  },
  select: {
    padding: 6,
    borderRadius: 6,
    border: "1px solid #ccc",
    width: "100%",
  },

  checkboxLabel: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    lineHeight: 1.2,
  },

  muted: { color: "#999" },

  subRowDrug: { paddingLeft: 22, color: "#444", fontStyle: "italic" },
  subRowArrow: {
    display: "inline-block",
    marginRight: 6,
    color: "#666",
    fontStyle: "normal",
  },

  otherInfoLabel: { display: "block", fontWeight: 600, marginBottom: 6 },
  textarea: {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 14,
    resize: "vertical",
  },

  errorRow: {
    padding: 10,
    borderBottom: "1px solid #eee",
    background: "#fff5f5",
  },
  errorText: { color: "crimson", fontSize: 12, fontWeight: 600 },

  dateShortcuts: {
    display: "flex",
    gap: 6,
    marginTop: 6,
    flexWrap: "wrap",
  },
  shortcutBtn: {
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#fff",
    cursor: "pointer",
    fontSize: 11,
  },
};